'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useQuery } from '@tanstack/react-query';
import { Plus, Star } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getPersonalizeFn,
  type Item as PersonalizeItem,
} from '@/api/use-get-personalize';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { LoginDialog } from '@/app/[locale]/(main)/store/components/fb-login';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { Link, useRouter } from '@/i18n/routing';
import { formatDistanceMeterKilometer } from '@/lib/eta-calculator';
import { trackEvent } from '@/lib/event-tracking';
import { ahafoodStore, useAuthStore } from '@/store';
import type { MenuItem } from '@/types/item';

const LIMIT = 100;

export default function ForYouItemList() {
  const { user } = useAuthStore();

  // Get store data
  const { address, city_id } = ahafoodStore();

  // Prepare API parameters
  const params = {
    customer_id: user?.cunstomerId ?? '',
    city_id: city_id ?? 'SGN',
    lat: address?.lat ?? 10.7769,
    lng: address?.lng ?? 106.7009,
    limit: 100,
  };
  const routes = useCityRoutes();
  const { isAuthenticated } = useAuthStore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  useEffect(() => {
    if (isAuthenticated) setShowLoginDialog(false);
  }, [isAuthenticated]);

  const { data, isLoading } = useQuery({
    queryKey: ['personalize-items', { params }],
    queryFn: () =>
      getPersonalizeFn({
        customer_id: params.customer_id,
        city_id: params.city_id,
        lat: params.lat,
        lng: params.lng,
        limit: LIMIT,
      }),
    enabled: !!params.customer_id && !!params.city_id,
  });

  const personalizeItems = useMemo(
    () => data?.items?.filter(Boolean) ?? [],
    [data]
  );

  const renderHeader = (
    <div className="mb-4">
      <h2 className="text-lg font-bold text-neutral-90 mb-1">
        Món ăn được đề xuất cho bạn
      </h2>
      <p className="text-sm text-neutral-60">
        Dựa trên sở thích và lịch sử của bạn
      </p>
    </div>
  );

  if (isLoading && !personalizeItems.length) {
    return (
      <div className="w-full">
        {renderHeader}
        <div className="grid grid-cols-1 gap-4">
          <PersonalizeItemSkeleton />
          <PersonalizeItemSkeleton />
          <PersonalizeItemSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {renderHeader}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {personalizeItems.length ? (
          personalizeItems.map((item) => (
            <PersonalizeItemCard
              location="for_you_zone"
              key={item._id}
              item={item}
              isAuthenticated={isAuthenticated}
              setShowLoginDialog={setShowLoginDialog}
            />
          ))
        ) : (
          <div className="text-center py-8 text-neutral-60">
            Không có món ăn nào.
          </div>
        )}
      </div>

      {personalizeItems.length > 0 && (
        <div className="text-center py-8">
          <Link href={routes.home}>
            <Button
              variant="outline"
              className="border-primary w-full font-bold text-primary"
            >
              Khám phá thêm món ăn
            </Button>
          </Link>
        </div>
      )}

      <LoginDialog open={showLoginDialog} onOpenChange={setShowLoginDialog} />
    </div>
  );
}

/* ----------------- Card ----------------- */
export function PersonalizeItemCard({
  location,
  item,
  isAuthenticated,
  setShowLoginDialog,
}: {
  location?: string;
  item: PersonalizeItem;
  isAuthenticated: boolean;
  setShowLoginDialog: (show: boolean) => void;
}) {
  const goToOrderWebstore = useGoToOrderWebstore();
  const routes = useCityRoutes();
  const router = useRouter();
  const isClosed = item.status !== 'ACTIVE';

  const trackClickEvent = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'for_you_card_click',
      button_location: location ?? 'for_you_zone',
    });
  };

  const { showRating, rawRating, showSold, soldDisplay, imageSrc } =
    useMemo(() => {
      const sold = item.total_sold;
      const showSold = sold > 10;
      const soldDisplay =
        sold > 999 ? `${(sold / 1000).toFixed(1)}K` : sold.toString();
      const rating =
        item.rating?.average ||
        (item.rating?.google_average >= 4 ? item.rating.google_average : null);

      const imageSrc =
        item.image_thumb_url ||
        `${assetPrefix}/static/images/empty-store-bot.webp`;

      return {
        showRating: !!rating,
        rawRating: rating,
        showSold,
        soldDisplay,
        imageSrc,
      };
    }, [item]);

  const handleOrderNow = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'for_you_add_to_cart',
        button_location: location ?? 'for_you_zone',
      });
      if (isClosed) return;
      if (!isAuthenticated) return setShowLoginDialog(true);

      const convertedItem: MenuItem = {
        _id: item._id,
        name: item.name,
        price: item.price,
        image_thumb_url: item.image_thumb_url,
        discounted_price: item.discount_price,
        is_ai_image: item.is_ai_image ?? false,
      };

      try {
        await goToOrderWebstore(
          item.store_id.toString(),
          item.slug ?? '',
          '',
          convertedItem,
          item.store_id?.toString() || '',
          'for_you'
        );
        router.push(routes.storeDetail(item.slug ?? ''));
      } catch (error) {
        console.error('Failed to go to order webstore:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuthenticated,
      isClosed,
      goToOrderWebstore,
      item,
      router,
      routes,
      setShowLoginDialog,
    ]
  );

  const handleCardClick = useCallback(() => {
    if (!isAuthenticated) return setShowLoginDialog(true);
    trackClickEvent();
    if (!isClosed) router.push(routes.storeDetail(item.slug ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isClosed,
    router,
    routes,
    setShowLoginDialog,
    item.slug,
  ]);

  return (
    <Card
      onClick={handleCardClick}
      style={{
        boxShadow:
          '0px 4px 4px 0px rgba(0, 0, 0, 0.07), 0px 4px 4px 0px #0000001F inset',
      }}
      className="p-3 pb-5 bg-white/20 backdrop-blur-xl cursor-pointer relative overflow-hidden rounded-3xl border-none"
    >
      {isClosed && (
        <div className="absolute inset-0 bg-neutral-50/50 z-20 rounded-2xl">
          <Badge className="absolute bg-neutral-90 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1.5 shadow-md font-bold">
            Tạm ngưng
          </Badge>
        </div>
      )}

      <div className="flex gap-3">
        {/* Content */}
        <div className="flex-1 flex flex-col justify-center min-w-0 pl-2">
          <div>
            <h3 className="font-semibold text-base text-neutral-90 line-clamp-2 mb-1">
              {item.name}
            </h3>

            {/* Store info */}
            <div className="flex items-center gap-1 text-xs text-neutral-60 mb-2">
              <span>{formatDistanceMeterKilometer(item.distance * 1000)}</span>
              <span className="truncate">• {item.page_name}</span>
            </div>

            {/* Rating & Sold */}
            {(showRating || showSold) && (
              <div className="flex items-center gap-3 text-xs text-neutral-60 mb-2">
                {showRating && (
                  <span className="flex items-center gap-1">
                    {item.rating?.total !== 0 ? (
                      <Star className="size-3 fill-[#FEBC2F] text-[#FEBC2F]" />
                    ) : (
                      <img
                        src={`${assetPrefix}/static/images/gg-rating.webp`}
                        alt="Rating"
                        className="h-3 w-auto"
                      />
                    )}
                    <span className="font-bold">{rawRating!.toFixed(1)}</span>
                  </span>
                )}
                {showSold && (
                  <span className="flex items-center gap-1">
                    <img
                      src={`${assetPrefix}/static/icons/bag.svg`}
                      alt="sold"
                      className="size-3"
                    />
                    {soldDisplay} lượt bán
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-2">
            <span className="font-bold text-lg text-primary">
              {formatCurrency(item.discount_price ?? item.price)}
            </span>
            {item.discount_price && item.discount_price < item.price && (
              <span className="text-xs text-neutral-40 line-through">
                {formatCurrency(item.price)}
              </span>
            )}
          </div>
        </div>

        {/* Image & Button */}
        <div className="relative flex-1 shrink-0">
          <div className="relative w-full aspect-square rounded-2xl overflow-hidden border-2 border-white">
            <Image
              src={imageSrc}
              alt={item.name}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 50vw, 128px"
            />
            {item.is_ai_image && (
              <Badge className="absolute top-2 right-2 size-6 bg-white/50 hover:bg-white/90 border-none shadow-sm p-1">
                <Image
                  fill
                  className="p-0.5"
                  src={`${assetPrefix}/static/icons/white-ai.svg`}
                  alt="AI"
                />
              </Badge>
            )}
          </div>
          <Button
            onClick={handleOrderNow}
            disabled={isClosed}
            className="absolute -bottom-2 right-1/2 translate-x-1/2 p-0 bg-primary-10 text-primary-50 font-semibold  rounded-xl shadow-lg hover:bg-primary-10 z-10 w-4/5 border border-primary-50"
          >
            <div className="size-full relative px-6 py-2 pt-2.5 h-9">
              Thêm
              <Plus className="absolute top-1 right-1" />
            </div>
          </Button>
        </div>
      </div>

      {/* AI suggestion badge */}
      {/* {item. && (
        <div className="mt-3 pt-3 border-t border-dashed border-neutral-30 flex items-start gap-2 text-xs text-neutral-70">
          <img
            src={`${assetPrefix}/static/icons/sparkle.svg`}
            alt="reason"
            className="size-4 mt-0.5"
          />
          <p className="line-clamp-2">{item.reason}</p>
        </div>
      )} */}
    </Card>
  );
}

/* ----------------- Skeleton ----------------- */
// Match compact vertical card (w-[150px] image-top) — liquid glass subtle
export function PersonalizeItemSkeleton() {
  return (
    <Card className="w-[170px] shrink-0 overflow-hidden rounded-2xl bg-gradient-to-b from-white/70 via-white/50 to-white/30 backdrop-blur-sm border border-white/60 shadow-[0_4px_16px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.6)] p-0">
      {/* Image square top */}
      <Skeleton className="aspect-square w-full rounded-none" />
      {/* Content compact */}
      <div className="px-2 pt-1.5 pb-2 flex flex-col gap-1.5">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-5 w-20 rounded-sm" />
        <div className="flex justify-between gap-1">
          <Skeleton className="h-3 flex-1" />
          <Skeleton className="h-3 w-10" />
        </div>
      </div>
    </Card>
  );
}
